import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Upload, Download, X, Car, Fuel, Gauge } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAMAGE_TYPES = [
  { id: 'scratch', label: 'Scratch', color: '#FF6B6B' },
  { id: 'dent', label: 'Dent', color: '#FFA500' },
  { id: 'chip', label: 'Chip/Stone', color: '#FFD93D' },
  { id: 'crack', label: 'Crack', color: '#C70039' },
  { id: 'other', label: 'Other', color: '#9B59B6' },
];

const FUEL_LEVELS = ['empty', '1/4', '1/2', '3/4', 'full'];

const VehicleConditionManager = ({ bookingId, vehicleName, onSave }) => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('pickup');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [conditionData, setConditionData] = useState(null);
  
  const [pickupCondition, setPickupCondition] = useState({
    odometer: '',
    fuel_level: 'full',
    damage_marks: [],
    notes: ''
  });
  
  const [returnCondition, setReturnCondition] = useState({
    odometer: '',
    fuel_level: 'full',
    damage_marks: [],
    notes: ''
  });

  const headers = { Authorization: 'Bearer ' + token };

  useEffect(function() {
    fetchConditionData();
  }, [bookingId]);

  function fetchConditionData() {
    axios.get(API_URL + '/api/admin/bookings/' + bookingId + '/condition-photos', { headers: headers })
      .then(function(response) {
        setConditionData(response.data);
        if (response.data.pickup_condition) {
          setPickupCondition({
            odometer: response.data.pickup_condition.odometer || '',
            fuel_level: response.data.pickup_condition.fuel_level || 'full',
            damage_marks: response.data.pickup_condition.damage_marks || [],
            notes: response.data.pickup_condition.notes || ''
          });
        }
        if (response.data.return_condition) {
          setReturnCondition({
            odometer: response.data.return_condition.odometer || '',
            fuel_level: response.data.return_condition.fuel_level || 'full',
            damage_marks: response.data.return_condition.damage_marks || [],
            notes: response.data.return_condition.notes || ''
          });
        }
        setLoading(false);
      })
      .catch(function(error) {
        console.error('Error:', error);
        setLoading(false);
      });
  }

  function handleSaveCondition(type) {
    setSaving(true);
    var condition = type === 'pickup' ? pickupCondition : returnCondition;
    
    axios.put(
      API_URL + '/api/admin/bookings/' + bookingId + '/condition/' + type,
      {
        odometer: condition.odometer ? parseInt(condition.odometer) : null,
        fuel_level: condition.fuel_level,
        damage_marks: condition.damage_marks,
        notes: condition.notes
      },
      { headers: headers }
    )
      .then(function() {
        toast.success((type === 'pickup' ? 'Pickup' : 'Return') + ' condition saved');
        setSaving(false);
        if (onSave) onSave();
      })
      .catch(function(error) {
        console.error('Error:', error);
        toast.error('Failed to save condition');
        setSaving(false);
      });
  }

  function addDamageMark(type, mark) {
    var setter = type === 'pickup' ? setPickupCondition : setReturnCondition;
    setter(function(prev) {
      return { ...prev, damage_marks: prev.damage_marks.concat([mark]) };
    });
  }

  function removeDamageMark(type, index) {
    var setter = type === 'pickup' ? setPickupCondition : setReturnCondition;
    setter(function(prev) {
      return { 
        ...prev, 
        damage_marks: prev.damage_marks.filter(function(_, i) { return i !== index; })
      };
    });
  }

  var currentCondition = activeTab === 'pickup' ? pickupCondition : returnCondition;
  var setCurrentCondition = activeTab === 'pickup' ? setPickupCondition : setReturnCondition;

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Loading condition data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl text-[#181B1F] font-light">Vehicle Condition</h2>
          <p className="text-gray-500 text-sm">{vehicleName}</p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="flex gap-2 bg-gray-50 border border-gray-200 p-1 rounded-lg w-fit">
          <button
            type="button"
            onClick={function() { setActiveTab('pickup'); }}
            className={'px-4 py-2 rounded-md text-sm font-medium transition-all ' + 
              (activeTab === 'pickup' ? 'bg-primary text-white' : 'text-gray-500 hover:text-[#181B1F] hover:bg-gray-100')}
          >
            Pickup Condition
          </button>
          <button
            type="button"
            onClick={function() { setActiveTab('return'); }}
            className={'px-4 py-2 rounded-md text-sm font-medium transition-all ' + 
              (activeTab === 'return' ? 'bg-primary text-white' : 'text-gray-500 hover:text-[#181B1F] hover:bg-gray-100')}
          >
            Return Condition
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-gray-500">Odometer (km)</Label>
            <Input
              type="number"
              value={currentCondition.odometer}
              onChange={function(e) { 
                setCurrentCondition(function(prev) { 
                  return { ...prev, odometer: e.target.value }; 
                }); 
              }}
              className="input-default"
              placeholder="Current reading"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-gray-500">Fuel Level</Label>
            <Select 
              value={currentCondition.fuel_level} 
              onValueChange={function(v) { 
                setCurrentCondition(function(prev) { 
                  return { ...prev, fuel_level: v }; 
                }); 
              }}
            >
              <SelectTrigger className="input-default">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                {FUEL_LEVELS.map(function(level) {
                  return (
                    <SelectItem key={level} value={level} className="text-[#181B1F]">
                      {level === 'empty' ? 'Empty' : level === 'full' ? 'Full' : level}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-gray-500">Notes</Label>
          <Textarea
            value={currentCondition.notes}
            onChange={function(e) { 
              setCurrentCondition(function(prev) { 
                return { ...prev, notes: e.target.value }; 
              }); 
            }}
            className="input-default"
            placeholder="Additional notes about vehicle condition..."
            rows={3}
          />
        </div>

        <div className="flex justify-end">
          <Button 
            onClick={function() { handleSaveCondition(activeTab); }} 
            disabled={saving} 
            className="btn-primary"
          >
            {saving ? 'Saving...' : 'Save Condition'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VehicleConditionManager;
